import React, { useContext, useMemo, useState } from 'react'
import styled from 'styled-components'
import hashids from 'helpers/dine-in/hashids'
import {
  BuffetItemTypeEnum,
  CheckoutMutation,
  KPaymentResultFailureCodeEnum,
  KPaymentResultStatusEnum,
  OrderInvalidEnum,
  PaymentTypeEnum,
  useCheckoutMutation,
  type PaymentMethodQuery,
} from 'graphQL/dinein/generate/operations'
import CheckoutPage, { CheckoutMode } from './CheckoutPage'
import { AppContext } from 'contexts/AppProvider'
import { useHistory } from 'react-router-dom'
import { SubPageName } from 'enums/buffet'
import { FetchResult } from '@apollo/client'
import { dineInClient } from 'variables/services'
import { useSelector } from 'react-redux'
import { buffetSelector, setPaymentMethod } from 'store/slices/buffetSlice'
import * as Sentry from '@sentry/browser'
import { isDevelopment, isProduction } from 'variables/environment'
import useCheckout, { InvalidOrder } from 'hooks/buffet/useCheckout'
import useBuffetCheckout from 'hooks/buffet/useBuffetCheckout'
import OverlayPage from 'components/dine-in/OverlayPage'
import TrueWalletPage from 'components/TrueWallet'
import { useTranslation } from 'react-i18next'
import ModalWarningTotalZeroDue from 'components/modals/ModalWarningTotalZeroDue'
import { Button, Fonts, Modal } from 'components/base-ui'
import { generateSrcSet } from 'helpers/image'
import { useAppDispatch } from 'store'
import ModalKPlusPhoneError from 'components/modals/ModalKPlusPhoneError'
import ModalKPromptPayPhoneError from 'components/modals/ModalKPromptPayPhoneError'
import ModalKPaymentProcessingError from 'components/modals/ModalKPaymentProcessingError'
import { weight } from 'components/base-ui/Fonts'
import { getOrderInvalidWarningText } from 'helpers/payment'
import { getErrorMessageOmiseCharge } from 'helpers/omiseCharge'
import useOmise from 'services/hooks/useOmise'
import ModalPaymentSuccess from 'components/dine-in/modal/ModalPaymentSuccess'

const warningImage = `${process.env.PUBLIC_URL}/assets/generic/modal/warning.png`

const ChekcoutController = () => {
  const { brand, customerCreditCard, isFoodPassion } = useContext(AppContext)
  const buffet = useSelector(buffetSelector)
  const dispatch = useAppDispatch()
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const isShowMemberCard = brand?.slug === 'party-service'
  const [trueWalletPhone, setTrueWalletPhone] = useState('')
  const [isNotValidTrueWalletPhone, setIsNotValidTrueWalletPhone] = useState(false)
  const [warningText, setWarningText] = useState('')
  const [warningTtile, setWarningTtile] = useState('')
  const [isLoadingModal, setIsLoadingModal] = useState(false)
  const [isShowModalError, setIsShowModalError] = useState(false)
  const [kpaymentPhoneError, setKpaymentPhoneError] = useState('')
  const [isOpenModalKPlusPhoneError, setIsOpenModalKPlusPhoneError] = useState(false)
  const [isOpenModalKPromptPayPhoneError, setIsOpenModalKPromptPayPhoneError] = useState(false)
  const [isOpenModalKPaymentProcessingError, setIsOpenModalKPaymentProcessingError] = useState(false)
  const [isOpenModalInvalidCoupon, setIsOpenModalInvalidCoupon] = useState(false)
  const [isOpenModalPaymentSuccess, setIsOpenModalPaymentSuccess] = useState(false)

  const history = useHistory()
  const {
    currentOrder,
    setCurrentOrder,
    vendor,
    buffetItems,
    paymentMethods,
    refreshBuffetOrder,
    handleCheckoutValidation,
    refetcBuffetFpCheckout,
    refetchBuffetHhCheckout,
  } = useBuffetCheckout({})
  const {
    isCheckoutProcessing,
    setIsCheckoutProcessing,
    validateCheckout,
    isShowTrueWallet,
    setIsShowTrueWallet,
    isCashVoucherErrorZerodue,
    setIsCashVoucherErrorZerodue,
  } = useCheckout({ trueWalletPhone })

  const refetchPayment: () => Promise<PaymentMethodQuery> = async () => {
    if (isFoodPassion) {
      const result = await refetcBuffetFpCheckout()
      return {
        currentOrder: result.data.orderAndFPMemberCoupons.order,
        ...result.data,
      }
    }

    const result = await refetchBuffetHhCheckout()
    return {
      currentOrder: result.data.orderAndHHMemberCoupons.order,
      ...result.data,
    }
  }

  const [checkout] = useCheckoutMutation({ client: dineInClient })
  const omise = useOmise({
    omisePublicKey: brand?.omisePublicKey ? brand.omisePublicKey : process.env.REACT_APP_OMISE_PUBLIC_KEY,
  })
  const omiseCreateToken = omise.createToken

  const { packageTotal, overcharge } = useMemo(() => {
    const packageTotal = buffetItems
      .filter(({ buffetItemType }) => buffetItemType === BuffetItemTypeEnum.Person)
      .reduce((total, { totalSatangs }) => {
        return totalSatangs + total
      }, 0)

    const overcharge = buffetItems
      .filter(
        ({ buffetItemType }) =>
          buffetItemType === BuffetItemTypeEnum.LeftOver || buffetItemType === BuffetItemTypeEnum.Overtime
      )
      .reduce((total, { totalSatangs }) => {
        return totalSatangs + total
      }, 0)
    return { packageTotal, overcharge }
  }, [buffetItems])

  const onCheckout = async () => {
    try {
      setIsCheckoutProcessing(true)
      await refreshBuffetOrder()
      const validateResult = handleCheckoutValidation(buffet.currentOrder)
      if (validateResult.isOK) {
        if (currentOrder) {
          const { isOK, invalid } = validateCheckout(currentOrder)
          if (isOK) {
            await submitPayment()
          } else {
            if (invalid === InvalidOrder.ShouldShowTrueWalletPage) {
              setIsShowTrueWallet(true)
            } else if (invalid === InvalidOrder.TrueWalletPhoneNumberIsEmpty) {
              setIsNotValidTrueWalletPhone(true)
            } else if (invalid === InvalidOrder.InvalidAdjustment) {
              setIsCashVoucherErrorZerodue(true)
            }
            setTimeout(() => setIsCheckoutProcessing(false), 300) // wait for animation
          }
        }
      }
    } catch (error) {
      setIsCheckoutProcessing(false)
      isProduction && Sentry.captureException(new Error(`Buffet Error from checkout button: ${JSON.stringify(error)}`))
    }
  }

  const validatePaymentResult = (
    paymentType: string,
    result: FetchResult<CheckoutMutation, Record<string, any>, Record<string, any>>
  ) => {
    let isSuccess = false
    const isTrueWallet = paymentType === PaymentTypeEnum.OmiseTrueMoneyWallet
    const KBankPromtpay = paymentType === PaymentTypeEnum.KPaymentPromptPayNumber
    const KPlusNumber = paymentType === PaymentTypeEnum.KPaymentKPlusNumber
    const isCreditCard = paymentType === PaymentTypeEnum.OmiseCreditCard

    if (result?.data) {
      const hasErrors = result?.errors?.length
      const kPaymentIsFailed = result.data.checkout?.KPaymentResult?.status === KPaymentResultStatusEnum.Fail
      const hasOrderInvalid = result.data.checkout.orderInvalids.length
      const trueWalletIsFailed = isTrueWallet && result.data.checkout?.chargeResult?.status === 'failed'
      const creditCardIsFailed = isCreditCard && result.data.checkout?.chargeResult?.status === 'failed'

      if (hasErrors) {
        let errorMessage = ''
        if (result?.errors) {
          errorMessage = result?.errors[0].message
        }
        if (isTrueWallet && errorMessage === 'Error CheckoutByCustomer: Error creating Omise QR Charge') {
          setIsNotValidTrueWalletPhone(true)
        }
        openModalError('', errorMessage)
        !isDevelopment && Sentry.captureException(new Error('Buffet error: submit payment'))
      } else if (kPaymentIsFailed) {
        const isInvalidPhoneNumber =
          result.data.checkout.KPaymentResult.failureCode === KPaymentResultFailureCodeEnum.InvalidPhone
        const isProcessingError =
          result.data.checkout.KPaymentResult.failureCode === KPaymentResultFailureCodeEnum.ProcessingError

        if (isInvalidPhoneNumber) {
          if (result.data) {
            setKpaymentPhoneError(result.data.checkout.KPaymentResult.phone)
          }
          if (KPlusNumber) {
            setIsOpenModalKPlusPhoneError(true)
          } else if (KBankPromtpay) {
            setIsOpenModalKPromptPayPhoneError(true)
          }
        } else if (isProcessingError) {
          setIsOpenModalKPaymentProcessingError(true)
        } else {
          if (result.data.checkout.KPaymentResult.failureMessage) {
            openModalError('', result.data.checkout.KPaymentResult.failureMessage)
          }
        }
      } else if (hasOrderInvalid) {
        if (result.data.checkout.orderInvalids.includes(OrderInvalidEnum.InvalidPromotion)) {
          setIsOpenModalInvalidCoupon(true)
        } else {
          const title = t('dineInSubmitPaymentError')
          const orderInvalidWarningText = getOrderInvalidWarningText(result.data.checkout.orderInvalids)
          openModalError(title, orderInvalidWarningText)
        }
      } else if (trueWalletIsFailed || creditCardIsFailed) {
        const errorMessage = getErrorMessageOmiseCharge(result.data.checkout.chargeResult.failureCode)
        if (errorMessage) {
          openModalError('', errorMessage)
        } else {
          openModalError('', result.data.checkout.chargeResult.failureMessage)
        }

        if (creditCardIsFailed) {
          omiseCreateToken({
            card: customerCreditCard,
            onSuccess: ({ token }) => {
              const paymentMethodWithNewToken = {
                ...buffet.paymentMethod,
                token,
              }
              dispatch(setPaymentMethod(paymentMethodWithNewToken))
            },
          })
        }
      } else {
        isSuccess = true
      }
    }

    return isSuccess
  }

  const submitPayment = async () => {
    if (currentOrder) {
      const { type } = buffet.paymentMethod!
      const isTrueWallet = type === PaymentTypeEnum.OmiseTrueMoneyWallet
      const isBankTransfer = type === PaymentTypeEnum.BankTransfer
      const isQRCode = type === PaymentTypeEnum.ScbPromptPay || type === PaymentTypeEnum.KBankThaiQr
      const isPromtpay = type === PaymentTypeEnum.OmisePromptPay
      const KBankPromtpay = type === PaymentTypeEnum.KPaymentPromptPayNumber
      const KPlusNumber = type === PaymentTypeEnum.KPaymentKPlusNumber
      const isCreditCard = type === PaymentTypeEnum.OmiseCreditCard
      try {
        const orderID = currentOrder.id
        const isZeroCart = currentOrder.totalDueSatangs === 0
        const orderIDEncoded = hashids.encode(buffet.table.orderID)
        const tableIDEncoded = hashids.encode(buffet.table.tableID)
        let result: FetchResult<CheckoutMutation, Record<string, any>, Record<string, any>> | null = null

        if (isZeroCart) {
          result = await checkout({
            variables: {
              orderID,
              payment: {
                type: PaymentTypeEnum.None,
              },
            },
          })
        } else if (isBankTransfer || isQRCode || isPromtpay) {
          result = await checkout({
            variables: {
              orderID,
              payment: {
                type,
              },
            },
          })
        } else if (isTrueWallet) {
          setIsCheckoutProcessing(true)
          const returnURI = `${window.location.origin}/buffet-pending-payment?table_id=${tableIDEncoded}&order_id=${orderIDEncoded}&paymentType=${type}`
          result = await checkout({
            variables: {
              orderID,
              payment: {
                type,
                phoneNumber: trueWalletPhone.replace(/-/g, ''),
                returnURI,
              },
            },
          })
        } else if (KBankPromtpay || KPlusNumber) {
          result = await checkout({
            variables: {
              orderID,
              payment: {
                type,
                phoneNumber: buffet.paymentMethod?.phoneNumber,
              },
            },
          })
        } else if (isCreditCard) {
          const returnURI = `${window.location.origin}/buffet-pending-payment?table_id=${tableIDEncoded}&order_id=${orderIDEncoded}&paymentType=${type}`
          result = await checkout({
            variables: {
              orderID,
              payment: {
                type,
                returnURI,
              },
            },
          })
        }

        let isSuccess = false
        if (result) {
          isSuccess = validatePaymentResult(type, result)
        }

        if (isSuccess) {
          if (result?.data?.checkout) {
            const checkoutResult = result.data.checkout
            const { chargeResult, order } = checkoutResult
            const isChargeResultSuccess = chargeResult?.status === 'successful'
            const isTrueWalletAndCreditCardPending =
              (isTrueWallet || isCreditCard) && chargeResult?.authorizeURI && !isChargeResultSuccess
            const isQRPaymentType = type === PaymentTypeEnum.KBankThaiQr || type === PaymentTypeEnum.OmisePromptPay
            if (isTrueWalletAndCreditCardPending) {
              window.location.href = result.data.checkout.chargeResult.authorizeURI
              return false
            } else if ((isCreditCard && isChargeResultSuccess) || isZeroCart) {
              setIsOpenModalPaymentSuccess(true)
            } else if (isQRPaymentType) {
              history.push(SubPageName.Home)
              if (result.data.checkout?.order) {
                setCurrentOrder({
                  ...currentOrder,
                  ...order,
                })
              }
            } else {
              history.push(SubPageName.Home)
            }
          } else {
            openModalError(t('dineInPaymentErrorTitle'), t('dineInPaymentErrorDesc'))
          }
        }
        setIsCheckoutProcessing(false)
      } catch (error) {
        setIsCheckoutProcessing(false)
        if (isTrueWallet) {
          setIsNotValidTrueWalletPhone(true)
        } else {
          openModalError(t('dineInPaymentErrorTitle'), t('dineInPaymentErrorDesc'))
        }

        if (isCreditCard) {
          omiseCreateToken({
            card: customerCreditCard,
            onSuccess: ({ token }) => {
              const paymentMethodWithNewToken = {
                ...buffet.paymentMethod,
                token,
              }
              dispatch(setPaymentMethod(paymentMethodWithNewToken))
            },
          })
        }

        !isDevelopment &&
          Sentry.captureException(new Error(`Buffet error from submit payment: ${JSON.stringify(error)}`))
      }
    }
  }

  const handleBackFromTrueWallet = () => {
    setIsShowTrueWallet(false)
    setIsNotValidTrueWalletPhone(false)
    setIsCheckoutProcessing(false)
    setTrueWalletPhone('')
  }

  const openModalError = (title: string, text: string) => {
    setWarningTtile(title)
    setWarningText(text)
    setTimeout(() => {
      setIsLoadingModal(false)
      setIsShowModalError(true)
    })
  }

  const onCloseModalError = () => {
    setTimeout(() => {
      setIsLoadingModal(false)
      setIsShowModalError(false)
      setIsShowTrueWallet(false)
    })
    setTimeout(() => {
      setWarningTtile('')
      setWarningText('')
    }, 500)
  }

  const onCloseModalInvalidCoupon = () => {
    setIsOpenModalInvalidCoupon(false)
    history.push(SubPageName.Promotions)
  }

  return vendor && currentOrder ? (
    <>
      <CheckoutPage
        mode={CheckoutMode.Buffet}
        handleBack={() => history.push(SubPageName.GlobalCart)}
        onClickDiscount={() => history.push(SubPageName.Promotions)}
        onClickPaymentItem={() => history.push(SubPageName.SelectPaymentMethod)}
        onCheckout={onCheckout}
        isShowMemberCard={isShowMemberCard}
        isCheckoutProcessing={isCheckoutProcessing}
        vendor={vendor}
        order={currentOrder}
        buffetLineItemsTotal={{ packageTotal, overcharge }}
        paymentMethods={paymentMethods}
        refetchPayment={refetchPayment}
      />

      <ModalWarningTotalZeroDue
        isOpen={isCashVoucherErrorZerodue}
        onClose={() => {
          setIsCheckoutProcessing(false)
          setIsCashVoucherErrorZerodue(false)
        }}
        onClickButton={() => {
          setIsCheckoutProcessing(false)
          setIsCashVoucherErrorZerodue(false)
          history.push(SubPageName.Promotions)
        }}
      />

      <ModalPaymentSuccess
        isOpen={isOpenModalPaymentSuccess}
        isLoading={isLoadingModal}
        onCloseModal={() => history.push(SubPageName.Home)}
        lang={language}
      />

      <Modal
        isOpen={isOpenModalInvalidCoupon}
        onRequestClose={() => setIsOpenModalInvalidCoupon(false)}
        zindex="9999"
        hideCloseButton={false}
      >
        <ModalContainer>
          <ModalTitleGeneric>{t('modalWarningInvalidCouponTitle')}</ModalTitleGeneric>
          <ModalText>{t('modalWarningInvalidCouponDes')}</ModalText>
          <Button md minWidth="175px" onClick={onCloseModalInvalidCoupon}>
            {t('modalWarningInvalidCouponButton')}
          </Button>
        </ModalContainer>
      </Modal>

      <Modal
        isOpen={isShowModalError}
        isLoading={isLoadingModal}
        onRequestClose={() => onCloseModalError()}
        shouldCloseOnOverlayClick={false}
        btnClose={t('btnOK')}
        hideCloseButton={false}
      >
        <ModalContent>
          <img alt="generic-icon-warning" src={warningImage} srcSet={generateSrcSet(warningImage, 'png')} />
          {warningTtile && <Fonts.H2 style={{ marginTop: '15px' }}>{warningTtile}</Fonts.H2>}
          {warningText && <Fonts.Base>{warningText}</Fonts.Base>}
        </ModalContent>
      </Modal>

      <ModalKPlusPhoneError
        phone={kpaymentPhoneError}
        isOpen={isOpenModalKPlusPhoneError}
        onRequestClose={() => setIsOpenModalKPlusPhoneError(false)}
      />

      <ModalKPromptPayPhoneError
        phone={kpaymentPhoneError}
        isOpen={isOpenModalKPromptPayPhoneError}
        onRequestClose={() => setIsOpenModalKPromptPayPhoneError(false)}
      />

      <ModalKPaymentProcessingError
        isOpen={isOpenModalKPaymentProcessingError}
        onRequestClose={() => setIsOpenModalKPaymentProcessingError(false)}
      />

      <OverlayPage isShow={isShowTrueWallet} animation>
        <TrueWalletPage
          onClickBack={handleBackFromTrueWallet}
          handlePlaceOrder={onCheckout}
          isRequestPlaceOder={isCheckoutProcessing}
          trueWalletPhone={trueWalletPhone}
          setTrueWalletPhone={setTrueWalletPhone}
          isError={isNotValidTrueWalletPhone}
          setIsNotValidTrueWalletPhone={setIsNotValidTrueWalletPhone}
        />
      </OverlayPage>
    </>
  ) : null
}

export default ChekcoutController

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 7px;
  padding-bottom: 20px;
  text-align: center;
`

const ModalContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`

const ModalTitleGeneric = styled(Fonts.H2)`
  margin-top: 15px;
  margin-bottom: 5px;
  text-align: center;
  color: black;
`

const ModalText = styled.div`
  font-weight: ${weight.normal};
  font-size: 14px;
  color: ${(props) => props.theme.colors.gray700};
  text-align: center;
  margin-top: 10px;
  margin-bottom: 18px;
  white-space: pre-wrap;
`
